import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Users, TrendingUp, Sparkles, Star } from "lucide-react";
import Logo from "../../assets/images/logo.png";
import HeroImg from "../../assets/images/hero-1.jpg";
import PartnerStepIndicator from "./PartnerStepIndicator";
import "./PartnerLayout.css";

export const PartnerLayout = ({
  children,
  currentStepIndex,
  customStepper,
  badgeText = "Partner Portal",
  title = "Become an AHD Partner",
  subtitle = "Join India's leading real estate and home services ecosystem to grow your business.",
  visualImage = HeroImg,
  visualBadge = "AHD Partner Ecosystem",
  visualTitle = "Grow your business with AHD",
  visualSubtitle = "Connect with customers, showcase your services, and grow your presence across the AHD ecosystem.",
  visualHighlights,
  compact = false,
  className = "",
  contentClassName = "",
}) => {
  const defaultHighlights = [
    {
      icon: Users,
      title: "Verified Customer Leads",
      desc: "Direct inquiries from genuine property buyers, tenants & homeowners",
    },
    {
      icon: TrendingUp,
      title: "Maximum Business Visibility",
      desc: "Priority ranking and targeted promotion in your operating city",
    },
    {
      icon: ShieldCheck,
      title: "Trusted Partner Profile",
      desc: "Official AHD verified badge to build instant client confidence",
    },
  ];

  const highlights = visualHighlights || defaultHighlights;

  return (
    <div className={`partner-split-layout ${compact ? "partner-split-layout--compact" : ""} ${className}`.trim()}>
      {/* LEFT SIDE: FORM & ONBOARDING (45-50%) */}
      <div className={`partner-split__left ${compact ? "partner-split__left--compact" : ""}`.trim()}>
        <div className={`partner-split__left-content ${compact ? "partner-split__left-content--compact" : ""} ${contentClassName}`.trim()}>
          {/* Header Branding */}
          <div className="partner-split__header">
            <Link to="/" className="partner-split__logo-link">
              <img src={Logo} alt="All Home Desire" className="partner-split__logo" />
            </Link>
            <span className="partner-split__badge">{badgeText}</span>
          </div>

          {/* Step Progress */}
          {customStepper ? (
            <div className="partner-split__stepper">
              {customStepper}
            </div>
          ) : currentStepIndex ? (
            <div className="partner-split__stepper">
              <PartnerStepIndicator currentStepIndex={currentStepIndex} />
            </div>
          ) : null}

          {/* Step Heading */}
          <div className="partner-split__titles">
            <h1 className="partner-split__title">{title}</h1>
            {subtitle && <p className="partner-split__subtitle">{subtitle}</p>}
          </div>

          {/* Dynamic Form Content */}
          <div className="partner-split__form-body">
            {children}
          </div>

          {/* Footer note */}
          <div className="partner-split__footer">
            <span>© {new Date().getFullYear()} All Home Desire. All rights reserved.</span>
            <div className="partner-split__footer-links">
              <Link to="/terms-conditions">Terms</Link>
              <span>•</span>
              <Link to="/privacy-policy">Privacy</Link>
              <span>•</span>
              <Link to="/contact">Support</Link>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE: LARGE VISUAL EXPERIENCE (50-55%) */}
      <div className={`partner-split__right ${compact ? "partner-split__right--compact" : ""}`.trim()}>
        <div
          className="partner-split__right-bg"
          style={{ backgroundImage: `url(${visualImage})` }}
        />
        <div className="partner-split__right-overlay" />

        <div className="partner-split__right-content">
          <div className="partner-visual-card">
            <div className="partner-visual-card__badge">
              <Sparkles size={14} />
              <span>{visualBadge}</span>
            </div>

            <h2 className="partner-visual-card__title">{visualTitle}</h2>
            <p className="partner-visual-card__subtitle">{visualSubtitle}</p>

            <div className="partner-visual-card__highlights">
              {highlights.map((item, idx) => {
                const IconComponent = item.icon || CheckCircle2;
                return (
                  <div key={idx} className="partner-visual-highlight">
                    <div className="partner-visual-highlight__icon">
                      <IconComponent size={18} />
                    </div>
                    <div className="partner-visual-highlight__text">
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social proof testimonial / rating */}
            <div className="partner-visual-social">
              <div className="partner-visual-social__stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />
                ))}
                <span className="partner-visual-social__score">4.9 / 5</span>
              </div>
              <p className="partner-visual-social__text">
                Trusted by 5,000+ real estate & home service professionals across India
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLayout;
